import React, { useRef, useState } from 'react';
import { Paperclip, Upload, Trash2, Loader2, Download, ExternalLink, Pencil, Check, X, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { ProductAttachment } from '../../types';
import { formatBytes } from '../../utils/allegati';

interface Props {
  sku: string;
  attachments: ProductAttachment[];
  onChange: (attachments: ProductAttachment[]) => void;
}

const MAX_SIZE = 20 * 1024 * 1024;

/**
 * Allegati PDF dell'articolo (Admin): carica, rinomina, elimina. Il nome è
 * quello che il cliente vede nella sezione "Documenti" della scheda
 * prodotto; i file si scaricano senza login (``products/attachment_service.py``).
 */
export function AdminProductAttachments({ sku, attachments, onChange }: Props) {
  const [name, setName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const base = `/api/products/admin/${encodeURIComponent(sku)}/attachments`;

  const handleUpload = async (file: File) => {
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Sono ammessi solo file PDF.');
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error('File troppo grande (max 20MB).');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', name.trim());
      const res = await api.post(base, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange([...attachments, res.data.attachment]);
      setName('');
      toast.success('Allegato caricato');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Errore durante il caricamento');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRename = async () => {
    if (!editing) return;
    const nuovo = editing.name.trim();
    if (!nuovo) { toast.error('Il nome è obbligatorio'); return; }
    setBusyId(editing.id);
    try {
      const res = await api.patch(`${base}/${editing.id}`, { name: nuovo });
      onChange(attachments.map(a => (a.id === editing.id ? { ...a, name: res.data.name } : a)));
      setEditing(null);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Errore durante la modifica');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setBusyId(id);
    try {
      await api.delete(`${base}/${id}`);
      onChange(attachments.filter(a => a.id !== id));
      toast.success('Allegato eliminato');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Errore durante l\'eliminazione');
    } finally {
      setBusyId(null);
      setConfirmDelete(null);
    }
  };

  const iconBtn = 'w-7 h-7 rounded-lg flex items-center justify-center transition-colors flex-shrink-0';

  return (
    <div className="rounded-xl border bg-white shadow-sm overflow-hidden" data-testid="admin-product-attachments">
      <div className="flex items-center gap-2 px-4 py-3 border-b bg-[hsl(var(--dtk-canvas))]">
        <Paperclip className="w-4 h-4 text-[hsl(214,58%,41%)]" />
        <h2 className="text-sm font-semibold tracking-tight">Allegati ({attachments.length})</h2>
        <span className="text-[10px] text-muted-foreground ml-auto">Visibili a tutti nella scheda prodotto</span>
      </div>

      <div className="p-4">
        {attachments.length === 0 ? (
          <p className="text-xs text-muted-foreground mb-3">Nessun allegato per questo articolo.</p>
        ) : (
          <ul className="space-y-2 mb-4">
            {attachments.map(att => {
              const isEditing = editing?.id === att.id;
              const busy = busyId === att.id;
              return (
                <li key={att.id} className="flex items-center gap-3 px-3 py-2 rounded-lg border bg-white" data-testid="admin-product-attachment-row">
                  <div className="w-8 h-8 rounded border flex flex-col items-center justify-center flex-shrink-0 bg-red-50 text-red-600 border-red-200">
                    <FileText className="w-3.5 h-3.5" />
                    <span className="text-[7px] font-bold leading-none mt-0.5">PDF</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <input
                        autoFocus
                        value={editing.name}
                        maxLength={120}
                        onChange={e => setEditing({ id: att.id, name: e.target.value })}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleRename();
                          if (e.key === 'Escape') setEditing(null);
                        }}
                        className="w-full h-7 px-2 rounded border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(206,54%,40%)]"
                        data-testid="admin-product-attachment-name-input"
                      />
                    ) : (
                      <div className="text-sm font-medium truncate">{att.name}</div>
                    )}
                    <div className="text-[10px] text-muted-foreground truncate">
                      {att.filename} · {formatBytes(att.size)} · {new Date(att.uploadedAt).toLocaleDateString('it-IT')}
                    </div>
                  </div>
                  {busy ? (
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground flex-shrink-0" />
                  ) : isEditing ? (
                    <>
                      <button onClick={handleRename} title="Salva" className={`${iconBtn} text-emerald-600 hover:bg-emerald-50`} data-testid="admin-product-attachment-save">
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => setEditing(null)} title="Annulla" className={`${iconBtn} text-muted-foreground hover:bg-secondary/60`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </>
                  ) : confirmDelete === att.id ? (
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className="text-[11px] text-red-600 font-medium">Eliminare?</span>
                      <button onClick={() => handleDelete(att.id)} className="h-7 px-2 rounded-lg bg-red-600 text-white text-[11px] font-medium hover:bg-red-700" data-testid="admin-product-attachment-confirm-delete">
                        Elimina
                      </button>
                      <button onClick={() => setConfirmDelete(null)} className="h-7 px-2 rounded-lg border text-[11px] hover:bg-secondary/60">
                        No
                      </button>
                    </div>
                  ) : (
                    <>
                      <a href={att.url} target="_blank" rel="noopener noreferrer" title="Apri" className={`${iconBtn} text-[hsl(214,58%,41%)] hover:bg-blue-50`}>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                      <a href={att.downloadUrl} title="Scarica" className={`${iconBtn} text-[hsl(214,58%,41%)] hover:bg-blue-50`}>
                        <Download className="w-3.5 h-3.5" />
                      </a>
                      <button onClick={() => setEditing({ id: att.id, name: att.name })} title="Rinomina" className={`${iconBtn} text-muted-foreground hover:bg-secondary/60`} data-testid="admin-product-attachment-rename">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => setConfirmDelete(att.id)} title="Elimina" className={`${iconBtn} text-red-400 hover:bg-red-50`} data-testid="admin-product-attachment-remove">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <div className="rounded-lg border border-dashed border-[hsl(206,54%,40%)] bg-blue-50/40 p-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              value={name}
              maxLength={120}
              onChange={e => setName(e.target.value)}
              placeholder="Nome mostrato, es. Scheda tecnica (facoltativo)"
              className="flex-1 h-8 px-2.5 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(206,54%,40%)]"
              data-testid="admin-product-attachment-new-name"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="h-8 px-4 rounded-lg bg-[hsl(214,58%,41%)] text-white text-xs font-medium flex items-center justify-center gap-1.5 hover:bg-[hsl(206,54%,28%)] transition-colors disabled:opacity-50"
              data-testid="admin-product-attachment-upload"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploading ? 'Caricamento…' : 'Carica PDF'}
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">Solo PDF, max 20MB. Senza nome si usa quello del file.</p>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          data-testid="admin-product-attachment-file-input"
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) handleUpload(file);
          }}
        />
      </div>
    </div>
  );
}
